import { MbCheckoutBillingFacade } from '../facades/mb-checkout-billing.facade'
import { Injectable, OnDestroy } from '@angular/core'
import { BehaviorSubject, combineLatest, EMPTY, Subscription } from 'rxjs'
import {
  CommandService,
  EventService,
  GlobalMessageService,
  GlobalMessageType,
  UserIdService
} from '@spartacus/core'
import { MbBillingInformation, MbPaymentModeSelection } from '../../models'
import { catchError, switchMap, take, tap, withLatestFrom } from 'rxjs/operators'
import { ActiveCartFacade } from '@spartacus/cart/base/root'
import { MbCheckoutBillingFormConnector } from '../connectors'
import {
  CheckoutBillingAddressSetEvent,
} from '../../analytics/events/mb-checkout.events'

@Injectable({
  providedIn: 'root'
})
export class MbCheckoutBillingService implements MbCheckoutBillingFacade, OnDestroy {

  subscription = new Subscription()

  private poNumberFieldInvalid = new BehaviorSubject(false)

  get isPoNumberFieldInvalid () {
    return this.poNumberFieldInvalid.asObservable()
  }

  private cardViewAndPaymentVisible = new BehaviorSubject(true)

  get isCardViewAndPaymentVisible () {
    return this.cardViewAndPaymentVisible.asObservable()
  }

  private displaySimpleButtons = new BehaviorSubject(true)

  get isDisplaySimpleButtons () {
    return this.displaySimpleButtons.asObservable()
  }

  private paymentModeSelection = new BehaviorSubject<MbPaymentModeSelection>({})
  get selectedPaymentModeSelection () {
    return this.paymentModeSelection.asObservable()
  }

  constructor (protected globalMessageService: GlobalMessageService,
               protected activeCartService: ActiveCartFacade,
               protected userIdService: UserIdService,
               protected commandService: CommandService,
               protected eventService: EventService,
               protected mbCheckoutBillingFormConnector: MbCheckoutBillingFormConnector) {
    this.subscription?.add(this.activeCartService.isGuestCart().pipe(
      tap(isGuestCart => {
        if (isGuestCart) {
          this.cardViewAndPaymentVisible.next(false)
          this.displaySimpleButtons.next(false)
        }
      })
    ).subscribe())
  }

  ngOnDestroy () {
    this.subscription?.unsubscribe()
  }

  openCardView (): void {
    this.cardViewAndPaymentVisible.next(true)
  }

  closeCardView (): void {
    this.cardViewAndPaymentVisible.next(false)
  }

  setSelectedPaymentMode (paymentModeSelection?: MbPaymentModeSelection) {
    if (paymentModeSelection) {
      this.paymentModeSelection.next(paymentModeSelection)
    }
  }

  notifyPoNumberFieldValidity (isInvalid: boolean) {
    this.poNumberFieldInvalid.next(isInvalid)
  }

  postBillingInformation (form: any, cartID: string, userID: string) {
    this.commandService.create(() =>
      combineLatest([this.userIdService.takeUserId(), this.activeCartService.takeActiveCartId()]).pipe(
        take(1),
        switchMap(([userId, cartId]) => this.mbCheckoutBillingFormConnector.post(form, cartID, userID).pipe(
          tap(() => {
            this.eventService.dispatch(
              {
                userId: userId,
                cartId: cartId,
                address: form,
                isSuccessful: true
              }, CheckoutBillingAddressSetEvent)
          }),
          catchError(error => {
            this.eventService.dispatch({ isSuccessful: false, error: error }, CheckoutBillingAddressSetEvent)
            return EMPTY
          })
        ))
      )).execute(undefined)
  }

  postBillingStepForm (billingInformation: MbBillingInformation) {
    if (!billingInformation) {
      this.globalMessageService.add({ key: 'address.error' }, GlobalMessageType.MSG_TYPE_ERROR)
      return
    }
    this.userIdService.getUserId().pipe(
      take(1),
      withLatestFrom(this.activeCartService.getActiveCartId()),
      tap(([userId, cartId]) => {
        if (cartId && userId) {
          this.postBillingInformation(billingInformation, cartId, userId)
        } else {
          this.globalMessageService.add({ key: 'contact.error' }, GlobalMessageType.MSG_TYPE_ERROR)
        }
      })
    ).subscribe()
  }
}
